import React, { useState, useEffect, useCallback } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import debounce from 'lodash.debounce';
import axios from 'axios';
import {
    AppBar,
    IconButton,
    TextField,
    MenuItem, useMediaQuery
} from "@mui/material";
import { defaultVariables } from "../../../../../config";
import RayaField from "../../../../components/elements/fields/RayaField";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../../config/design/styles";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { connect } from "trim-redux";
import clsx from "clsx";
import ShopChooser from "../../../supplier/products/components/ShopChooser";
import { isAdmin } from "../../../../../config/Permissions";
import {theme} from "../../../../../config/design/theme";

const ListFilters = ({ onFilter, filters, classes, inner_item, has_bg, isApp }) => {
    const { t } = useTranslation();

    const [searchText, setSearchText] = useState('');
    const [industries, setIndustries] = useState([]);
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [shop_id, setShopId] = useState(null);
    const [dateFilter, setDateFilter] = useState('all');
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    const loginToken = localStorage.getItem('ACT');
    const config = {
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${loginToken}`
        }
    }

    useEffect(() => {
        const fetchIndustries = async () => {
            try {
                const response = await axios.get(`${defaultVariables.url}/industries/get-all`, config);
                const industryList = response.data;
                setIndustries(industryList);
                if (filters.includes('all')) {
                    setSelectedIndustry('all')
                } else if (industryList.length > 0) {
                    setSelectedIndustry(industryList[0].id);
                }
            } catch (error) {
                console.error('Error fetching industries:', error);
            }
        };

        fetchIndustries();
    }, []);

    const debouncedSearch = useCallback(
        debounce((text) => {
            onFilter({ text, industry: selectedIndustry, shop_id, date: dateFilter });
        }, 500),
        [onFilter, selectedIndustry, shop_id, dateFilter]
    );

    const handleSearchChange = (event) => {
        const text = event.target.value;
        setSearchText(text);
        debouncedSearch(text);
    };

    const handleClearSearch = () => {
        setSearchText("");
        debouncedSearch("");
    };

    const handleIndustrySelect = (industryId) => {
        setSelectedIndustry(industryId);
        onFilter({ text: searchText, industry: industryId, shop_id, date: dateFilter });
    };

    const handleShop = (shop) => {
        if (!!shop) {
            setShopId(shop.id);
            onFilter({ text: searchText, industry: selectedIndustry, shop_id: shop.id, date: dateFilter });
        }
    };

    const handleDateChange = (e) => {
        const value = e.target.value;
        setDateFilter(value);
        onFilter({ text: searchText, industry: selectedIndustry, shop_id, date: value });
    };

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <AppBar
            className={clsx(
                (!!filters.includes('industries') || !!inner_item || !!has_bg)
                    ? classes.panelAppBar
                    : classes.panelAppBarOnlyOne,
                !!isApp && "app-filter"
            )}
            position="static"
            color="gray"
        >
            <Grid container spacing={2} alignItems="center">
                {
                    (!!filters.includes('industries') || !!inner_item) &&
                    <Grid item size={{ xs: 12, md: 6, lg: 5 }}>
                        {!!filters.includes('industries') && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {!!filters.includes('all') && (
                                    <Button
                                        color="primary"
                                        variant={selectedIndustry === "all" ? 'contained' : 'outlined'}
                                        onClick={() => handleIndustrySelect("all")}
                                    >
                                        {t("all")}
                                    </Button>
                                )}
                                {industries.map((industry) => (
                                    <Button
                                        key={industry.id}
                                        color="primary"
                                        variant={selectedIndustry === industry.id ? 'contained' : 'outlined'}
                                        onClick={() => handleIndustrySelect(industry.id)}
                                    >
                                        {t(industry.name)}
                                    </Button>
                                ))}
                            </Box>
                        )}
                        {inner_item}
                    </Grid>
                }
                {
                   ( !!filters.includes('industries') && !filters.includes('time') && !filters.includes('shops') && !isTablet) &&
                    <Grid item size={{ xs: 12, md: 12, lg: 4 }} />
                }
                <Grid item size={{ xs: 12, md: 6, lg: 3 }}>
                    {!!filters.includes('search') && (
                        <RayaField
                            placeholder={t('search')}
                            variant="outlined"
                            fullWidth
                            value={searchText}
                            onChange={handleSearchChange}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {!searchText ? (
                                                <Search />
                                            ) : (
                                                <IconButton onClick={handleClearSearch}>
                                                    <X />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    )}
                </Grid>
                {!!filters.includes('time') && (
                    <Grid item size={{ xs: 12, md: 6, lg: 2 }}>
                        <RayaField
                            select
                            fullWidth
                            label={t('timeRange')}
                            value={dateFilter}
                            onChange={handleDateChange}
                        >
                            <MenuItem value="all">{t('all')}</MenuItem>
                            <MenuItem value="today">{t('today')}</MenuItem>
                            <MenuItem value="yesterday">{t('yesterday')}</MenuItem>
                            <MenuItem value="this_month">{t('thisMonth')}</MenuItem>
                            <MenuItem value="prev_month">{t('prev_month')}</MenuItem>
                            <MenuItem value="last_30_days">{t('last_30_days')}</MenuItem>
                        </RayaField>
                    </Grid>
                )}
                {!!filters.includes('shops') && !!isAdmin() && (
                    <Grid item size={{ xs: 12, md: 6, lg: 2 }}>
                        <ShopChooser
                            handleRes={handleShop}
                            default_id={shop_id}
                            industry={selectedIndustry}
                        />
                    </Grid>
                )}
            </Grid>
        </AppBar>
    );
};

const mstp = (state) => ({
    isApp: state.isApp,
});

export default withStyles(Styles)(connect(mstp)(ListFilters));
